// src/components/AuthScreen.js
import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input'; // Input is needed for email/password fields
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Loader2, Mail } from 'lucide-react'; // Removed Globe, kept Mail
import { cn } from '../lib/utils';

// Import Firebase auth functions
import {
    auth,
    signInWithGoogle,
    signInWithEmailAndPassword, // Re-enabled for conditional use
} from '../firebase/firebase';
import { FirebaseError } from 'firebase/app'; // Imported FirebaseError directly from firebase/app
import { toast } from 'sonner';

const AuthScreen = () => {
    // State for email and password, now conditionally used
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleGoogleSignIn = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await signInWithGoogle();
            toast.success('Signed in with Google successfully!');
        } catch (err) {
            console.error("Google Sign-in Error:", err);
            if (err instanceof FirebaseError) {
                setError(`Firebase Authentication Error: ${err.message}`);
                toast.error('Authentication Error', { description: err.message });
            } else {
                setError(`An unexpected error occurred: ${err.message}`);
                toast.error('Sign-in Failed', { description: err.message });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Conditionally defined Email/Password Sign-In handler
    const handleEmailPasswordSignIn = useCallback(async (e) => {
        e.preventDefault(); // Prevent default form submission
        setLoading(true);
        setError(null);
        try {
            // signInWithEmailAndPassword is already wrapped in firebase.js
            // to also call saveUserProfile
            await signInWithEmailAndPassword(auth, email, password);
            toast.success('Signed in with Email successfully!');
        } catch (err) {
            console.error("Email/Password Sign-in Error:", err);
            if (err instanceof FirebaseError) {
                setError(`Firebase Authentication Error: ${err.message}`);
                toast.error('Authentication Error', { description: err.message });
            } else {
                setError(`An unexpected error occurred: ${err.message}`);
                toast.error('Sign-in Failed', { description: err.message });
            }
        } finally {
            setLoading(false);
        }
    }, [email, password]); // Dependencies for useCallback


    return (
        <div className="min-h-screen bg-mad-timberwolf flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-mad-gunmetal text-mad-timberwolf shadow-xl rounded-xl border border-mad-dart-green">
                <CardHeader className="text-center pb-6">
                    {/* Updated to place logo next to title */}
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src={process.env.PUBLIC_URL + "/mad_owl_logo.png"} alt="MAD Owl Logo" className="h-16 w-auto"/>
                        <CardTitle className="text-3xl font-bold text-mad-fulvous">Welcome to MAD</CardTitle>
                    </div>
                    <CardDescription className="text-mad-peel text-lg">Method A Day - Your Daily Coding Challenge</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {error && (
                        <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 rounded-lg">
                            <AlertTitle>Authentication Error</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <Button
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className={cn(
                            "w-full bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg",
                            "hover:bg-blue-700 transition-colors duration-200",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            "flex items-center justify-center gap-2"
                        )}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            </>
                        ) : (
                            // Using the custom G-logo.svg image
                            <img src={process.env.PUBLIC_URL + "/G-logo.svg"} alt="Google G Logo" className="h-5 w-5" />
                        )}
                        Sign In with Google
                    </Button>

                    {/* Conditionally render email/password login for development */}
                    {process.env.NODE_ENV !== 'production' && (
                        <>
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-mad-dart-green" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-mad-gunmetal px-2 text-mad-timberwolf">
                                        Or
                                    </span>
                                </div>
                            </div>

                            <form onSubmit={handleEmailPasswordSignIn} className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="sr-only">Email</label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-black/80 text-white border-mad-dart-green rounded-md"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="password" className="sr-only">Password</label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-black/80 text-white border-mad-dart-green rounded-md"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className={cn(
                                        "w-full bg-mad-fulvous text-white px-6 py-3 rounded-full shadow-lg",
                                        "hover:bg-mad-fulvous/80 transition-colors duration-200",
                                        "disabled:opacity-50 disabled:cursor-not-allowed",
                                        "flex items-center justify-center gap-2"
                                    )}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="w-5 h-5" />
                                            Sign In with Email
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AuthScreen;
